// FaceLock Scan platform branches: phone or computer, the camera (asked for
// only after a tap), the feature flags, and the small cues (a soft tone, a
// short buzz on Android) when a checklist bucket completes.

import { scanTestHooks } from "./test-hooks";

export type DeviceKind = "ios" | "android" | "desktop";

export function deviceKind(nav: Pick<Navigator, "userAgent" | "maxTouchPoints"> | undefined = typeof navigator !== "undefined" ? navigator : undefined): DeviceKind {
  const ua = nav?.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  // iPadOS asks for the desktop site: a Mac with a touch screen is an iPad.
  if (/macintosh/i.test(ua) && (nav?.maxTouchPoints ?? 0) > 1) return "ios";
  if (/mobi/i.test(ua)) return "android";
  return "desktop";
}

export const isPhone = (k: DeviceKind = deviceKind()) => k !== "desktop";

/** Build flags (Vite env), read when asked so tests can flip them. On the dev server the real-browser harness can also turn one on (test-hooks.ts; never in a build). */
export function flag(name: "VITE_FACELOCK_SCAN_HANDOFF" | "VITE_FACELOCK_SCAN_SERVER_CHECK"): boolean {
  try {
    // The DEV test here, not only inside scanTestHooks(): a build then has no call left, so test-hooks.ts is dropped
    // whole, from the code and from the shipped source map (test-hooks.build.test.ts).
    if (import.meta.env.DEV && scanTestHooks()?.flags?.includes(name)) return true;
    const v = String(import.meta.env[name] ?? "");
    return v === "1" || v === "true";
  } catch {
    return false;
  }
}

export function prefersReducedMotion(): boolean {
  try {
    return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export type CameraFailure = "unsupported" | "denied" | "notFound" | "busy" | "tooSmall" | "other";

export class CameraError extends Error {
  reason: CameraFailure;
  constructor(reason: CameraFailure, message?: string) {
    super(message || reason);
    this.reason = reason;
  }
}

/** The shortest side a camera must deliver: a 512 px head-and-shoulders square has to fit in it. */
export const CAMERA_MIN_SIDE = 512;

export function cameraFailureOf(e: unknown): CameraFailure {
  if (e instanceof CameraError) return e.reason;
  const name = (e as { name?: string } | null)?.name || "";
  if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") return "denied";
  if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") return "notFound";
  if (name === "NotReadableError" || name === "TrackStartError" || name === "AbortError") return "busy";
  return "other";
}

/** Can this browser even ask for a camera? (No = the photo path, no tap needed.) */
export function cameraSupported(): boolean {
  return typeof navigator !== "undefined" && !!navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === "function";
}

/**
 * What the scan asks of the camera: 720p (1280 x 720, portrait on a phone) at
 * up to 30 frames a second. Enough for every kept picture (a head-and-shoulders
 * square of 512-720 px, the whole frame for the body) while a 1080p or 4K
 * stream, decoded 30-60 times a second, is what made laptops and mid-range
 * phones lag. The model never sees this size: it gets a 320-640 px copy
 * (capture.ts analysisFrame); the kept picture is cut from this full frame at
 * the moment it is kept.
 */
export const SCAN_CAMERA: MediaTrackConstraints = {
  facingMode: "user",
  // Ideals only: a hard limit a camera cannot meet would refuse to start it.
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 30 },
};

/** The front camera, at SCAN_CAMERA. Call from a tap (iPhone). */
export async function openFrontCamera(): Promise<MediaStream> {
  if (!cameraSupported()) throw new CameraError("unsupported");
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: false, video: SCAN_CAMERA });
  } catch (e) {
    throw new CameraError(cameraFailureOf(e), e instanceof Error ? e.message : String(e));
  }
}

export function stopStream(stream: MediaStream | null | undefined): void {
  try {
    stream?.getTracks().forEach((t) => { try { t.stop(); } catch { /* stopped */ } });
  } catch { /* nothing to stop */ }
}

// ------------------------------------------------------------ the cues --

let audio: AudioContext | null = null;

/** Create (or wake) the audio context inside a tap, so iPhone lets it play later. */
export function unlockSound(): void {
  try {
    const g = globalThis as typeof globalThis & { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
    const Ctx = g.AudioContext || g.webkitAudioContext;
    if (!Ctx) return;
    audio ??= new Ctx();
    if (audio.state === "suspended") void audio.resume();
  } catch {
    audio = null;
  }
}

/** A soft two-note tone (a bucket done), or a longer one (a clip done). */
export function chime(kind: "bucket" | "clip" = "bucket"): void {
  try {
    if (!audio) return;
    const notes = kind === "clip" ? [660, 880, 990] : [740, 988];
    notes.forEach((hz, i) => {
      const o = audio!.createOscillator();
      const g = audio!.createGain();
      const at = audio!.currentTime + i * 0.09;
      o.type = "sine";
      o.frequency.value = hz;
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.06, at + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.12);
      o.connect(g).connect(audio!.destination);
      o.start(at);
      o.stop(at + 0.13);
    });
  } catch { /* no sound is fine */ }
}

/** A short buzz where the phone can (Android; iPhone ignores it). */
export function buzz(ms = 25): void {
  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") navigator.vibrate(ms);
  } catch { /* no buzz is fine */ }
}
